/**
 * Native-fetch wrapper for Nado's gateway — outbound third-party
 * calls that api-base's Fun-API-shaped `sendRequest` doesn't fit.
 */

import type { FunLogger } from '../../src/utils/funLogger'
import { NadoGatewayError } from './errors'

export interface NadoFetchParams {
  uri: string
  method: 'GET' | 'POST'
  body?: object
}

/**
 * The per-call concerns every Nado request carries: core has no module-scoped
 * logger (each platform passes its own), and react-query supplies the signal.
 */
export interface NadoRequestOptions {
  logger: FunLogger
  signal?: AbortSignal
}

/** Full-call backstop — a stalled connection must not hang the signing flow
 * or silently freeze react-query polls. */
const DEFAULT_TIMEOUT_MS = 30_000

/** Bounded log payloads — full bodies overflow the Datadog browser-log queue. */
const LOGGED_BODY_MAX_CHARS = 500

/**
 * Our own timeout abort. A plain `Error` rather than a `DOMException`, which is
 * not a dependable global off-web — and only the `name` is ever read.
 */
class NadoRequestTimeoutError extends Error {
  constructor() {
    super('Nado request timed out')
    this.name = 'TimeoutError'
  }
}

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError'
}

/**
 * Timeout backstop combined with the caller's signal — react-query aborts on
 * unmount, never on a timer. Manual AbortController because older WebViews
 * lack `AbortSignal.timeout`/`AbortSignal.any`.
 */
function createAbortWithTimeout(
  callerSignal: AbortSignal | undefined,
  ms: number,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController()
  const id = setTimeout(
    () => controller.abort(new NadoRequestTimeoutError()),
    ms,
  )
  const propagateAbort = () => controller.abort(callerSignal?.reason)
  if (callerSignal?.aborted) {
    propagateAbort()
  } else {
    callerSignal?.addEventListener('abort', propagateAbort)
  }
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(id)
      callerSignal?.removeEventListener('abort', propagateAbort)
    },
  }
}

/**
 * Returns the parsed JSON body of a 2xx response; every other outcome throws
 * NadoGatewayError. Emits one bounded log event per call.
 */
export async function nadoFetch<T>(
  params: NadoFetchParams,
  { logger, signal }: NadoRequestOptions,
): Promise<T> {
  const { uri, method, body } = params
  const startTimeMs = Date.now()
  const abort = createAbortWithTimeout(signal, DEFAULT_TIMEOUT_MS)

  let response: Response
  let responseText: string
  try {
    response = await fetch(uri, {
      method,
      signal: abort.signal,
      ...(body !== undefined && {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    })
    // Inside the try — the body stream can fail or abort mid-read too
    responseText = await response.text()
  } catch (error) {
    // Caller-driven aborts (unmount, key change) are routine — never logged.
    // Everything else is warn: this layer can't know if a retry recovers;
    // ERROR is reserved for the flow that finally gives up.
    if (!signal?.aborted) {
      logger.warn('nado:fetch_error', {
        uri,
        method,
        durationMs: Date.now() - startTimeMs,
        error,
      })
    }
    throw new NadoGatewayError(
      `An error occurred trying to reach Nado: ${error}`,
      { kind: isTimeoutError(error) ? 'timeout' : 'transport' },
    )
  } finally {
    abort.cleanup()
  }

  if (!response.ok) {
    logger.warn('nado:fetch_failed', {
      uri,
      method,
      status: response.status,
      durationMs: Date.now() - startTimeMs,
      bodyPreview: responseText.slice(0, LOGGED_BODY_MAX_CHARS),
    })
    /**
     * Weight-based limits: queries 2400/min per IP, executes 600/min per
     * wallet. Hook poll cadences are budgeted against these (see hooks.ts).
     *
     * @see {@link https://docs.nado.xyz/developer-resources/api/rate-limits | Nado rate limits}
     */
    throw new NadoGatewayError(
      response.status === 429
        ? 'Nado rate limit exceeded'
        : `Nado request failed with status ${response.status}`,
      { kind: 'http', httpStatus: response.status },
    )
  }

  let json: unknown
  try {
    json = JSON.parse(responseText)
  } catch {
    json = undefined
  }
  // == also catches a literal "null" body — both would crash response parsers
  if (json == null) {
    logger.warn('nado:fetch_failed', {
      uri,
      method,
      status: response.status,
      bodyPreview: responseText.slice(0, LOGGED_BODY_MAX_CHARS),
    })
    // Transport: the body never arrived intact (proxy/captive portal/truncation)
    throw new NadoGatewayError('Nado returned an invalid success response', {
      kind: 'transport',
      httpStatus: response.status,
    })
  }

  // debug, not info — polls would emit ~10 events/min per open modal
  logger.debug('nado:fetch', {
    uri,
    method,
    status: response.status,
    durationMs: Date.now() - startTimeMs,
    bytes: responseText.length,
  })
  return json as T
}
